import type { Accommodation } from "@@/shared/acquire/accommodation";
import type { AccommodationAvailabilitySettings } from "@@/shared/acquire/accommodation-availability";
import { isRateValidFor } from "@@/shared/acquire/accommodation-engine/adiabatic-engine";
import type { CalculatedRatePrice } from "@@/shared/acquire/accommodation-engine/generate-accommodation-feed";
import type { AccommodationQuantities } from "@@/shared/acquire/accommodation-quantity";
import type { AccommodationQuantityType } from "@@/shared/acquire/accommodation-quantity-types";
import { getAccommodationRateOptionType } from "@@/shared/acquire/accommodation-rate-option-applies";
import type { PickedAccommodationType } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { isResourceAccommodation } from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import type { ResourceGraph } from "@@/shared/acquire/resources/resource-graph";
import type { ResourceGroup } from "@@/shared/acquire/resources/resource-group";
import type { ResourceId } from "@@/shared/acquire/resources/resource-id";
import type { ResourceReservationRequestInfo } from "@@/shared/acquire/resources/resource-reservation-info";
import {
    calculateAvailabilitySpecialized,
    type ResourceAvailability,
    type TimeRangeResourceAvailability,
} from "@@/shared/acquire/sessions/booking-helpers";
import { findAllGraphs } from "@@/shared/acquire/sessions/flatten-graph";
import { getPossibilities } from "@@/shared/acquire/sessions/verify-requested-quantities-are-possible-to-distribute-among-resources";
import { assertNever } from "@@/shared/assert-never";
import { asDate } from "@@/shared/date-time/as-date";
import { dateTimeFormatter } from "@@/shared/date-time/date-time-formatter";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    timeRangeFactory,
    type TimeRange,
} from "@@/shared/date-time/time-range";
import type { Timezone } from "@@/shared/date-time/timezone";
import { tz } from "@@/shared/date-time/timezone";
import { metaContentFactory } from "@@/shared/meta-content/meta-content";
import {
    orderItemAccommodationFactory_V2,
    orderItemAccommodationRateFactory_V2,
} from "@@/shared/orders/order-items/order-item-v2";
import { multiplyPrice } from "@@/shared/prices/multiply-price";
import { zeroPrice } from "@@/shared/prices/price";
import type { Product } from "@@/shared/products/product";
import { joinTranslatables } from "@@/shared/translations/text-utils";
import { defaultAmount } from "@@/shared/units-and-quantities/amount";
import { defaultTimeZone } from "@@/shared/weekly-schedules/default-time-zone";
import { exists } from "@@/utilities/exists";
import { sortBy } from "@@/utilities/sort";
import { addDays, startOfDay } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

const currentTimezoneName: Timezone = (Intl.DateTimeFormat()?.resolvedOptions()
    ?.timeZone || tz.EuropeStockholm) as Timezone;

const convertDate = (date: IsoAndUnixTimestamp, timeZone: Timezone) => {
    const dateZoned = toZonedTime(
        fromZonedTime(asDate(date), currentTimezoneName),
        timeZone,
    );
    return dateZoned;
};

const adjustTimeForAccommodation = (
    time: TimeRange,
    checkInTime: { hour: number; minute: number },
    checkOutTime: { hour: number; minute: number },
) => {
    const startDay = startOfDay(convertDate(time.start, defaultTimeZone));
    const endDay = startOfDay(convertDate(time.end, defaultTimeZone));

    const checkIn = new Date(
        startDay.getFullYear(),
        startDay.getMonth(),
        startDay.getDate(),
        checkInTime.hour,
        checkInTime.minute,
    );

    const checkOut = new Date(
        endDay.getFullYear(),
        endDay.getMonth(),
        endDay.getDate(),
        checkOutTime.hour,
        checkOutTime.minute,
    );

    const correctTime = timeRangeFactory(
        fromZonedTime(checkIn, defaultTimeZone),
        fromZonedTime(checkOut, defaultTimeZone),
    );
    return correctTime;
};

const adjustTimeForAccommodationSettings = (
    time: TimeRange,
    settings: AccommodationAvailabilitySettings,
) => {
    return adjustTimeForAccommodation(
        time,
        settings.checkInTime,
        settings.checkOutTime,
    );
};

const accommodationOrderItemFactory = (params: {
    time: TimeRange;
    accommodation: Accommodation;
    resource: ResourceGroup;
    accommodationType: PickedAccommodationType;
    rate: CalculatedRatePrice[];
    product: Product;
    numberOfAccommodations: number;
    quantities: AccommodationQuantities[];
    ignoreCapacity?: boolean; //Ignore capacity check for channex as some channels can't handle it
}) => {
    const { time, accommodation, resource, rate: calculated, product } = params;

    const firstRate = calculated[0];

    const correctTime = time;

    const keys: AccommodationQuantityType[] = Object.keys(
        accommodation.quantities,
    ) as AccommodationQuantityType[];

    const quantities = keys.flatMap(key => {
        const value = accommodation.quantities[key];
        if (!value) return [];

        switch (key) {
            case "ADULT":
                return accommodation.quantities.ADULT
                    ? {
                          type: accommodation.quantities.ADULT,
                          quantity: {
                              amount: defaultAmount,
                              value: firstRate.quantities.ADULT,
                          },
                      }
                    : [];
            case "CHILD":
                return accommodation.quantities.CHILD
                    ? {
                          type: accommodation.quantities.CHILD,
                          quantity: {
                              amount: defaultAmount,
                              value: firstRate.quantities.CHILD,
                          },
                      }
                    : [];
            case "INFANT":
                return accommodation.quantities.INFANT
                    ? {
                          type: accommodation.quantities.INFANT,
                          quantity: {
                              amount: defaultAmount,
                              value: firstRate.quantities.INFANT,
                          },
                      }
                    : [];
            case "GUEST":
                return accommodation.quantities.GUEST
                    ? {
                          type: accommodation.quantities.GUEST,
                          quantity: {
                              amount: defaultAmount,
                              value: firstRate.quantities.GUEST,
                          },
                      }
                    : [];
            case "ANIMAL":
                return accommodation.quantities.ANIMAL
                    ? {
                          type: accommodation.quantities.ANIMAL,
                          quantity: {
                              amount: defaultAmount,
                              value: firstRate.quantities.ANIMAL,
                          },
                      }
                    : [];
            default:
                assertNever(key);
        }
    });

    const sku = product.skus[0];
    if (!sku) throw new Error("No sku found");
    if (sku.acquire._type !== "ACCOMMODATION")
        throw new Error("No accommodation acquire found");

    const room = orderItemAccommodationFactory_V2({
        time: correctTime,
        meta: metaContentFactory({
            title: product.meta.title,
            kicker: product.meta.kicker,
            subtitle: product.meta.title,
        }),
        price: zeroPrice,
        providerId: product.providerId,
        quantity: {
            amount: accommodation.amount,
            value: params.numberOfAccommodations,
        },
        messageToProvider: null,
        discounts: [],
        pricePerItem: zeroPrice,
        productId: product._id,
        skuId: product.skus[0]._id,
        accommodation: {
            title: resource.meta.title,
            resourceId: resource._id,
            rateId: firstRate.rateWithPrices.rateId, //TODO remove?
            type: { ...params.accommodationType },
        },
        quantities: quantities,
        settings: accommodation.availability.baseSettings.settings,
        hasAccess: !!sku.acquire.hasAccess,
    });

    const allRates = params.quantities.flatMap(quantities => {
        const validRates = calculated
            .filter(
                calculated =>
                    isRateValidFor({
                        rate: calculated.rateWithPrices,
                        quantities,
                        timeRange: time,
                    }) || params.ignoreCapacity,
            )
            .sort(sortBy(r => r.price.amountIncludingVat, "ASCENDING")); //Get the cheapest rate

        const validRate = validRates[0];

        if (!validRate) throw new Error("No valid rate found");

        const lineItem = validRate.rateLineItems.flatMap(rl => {
            let title = rl.option.title;

            const nightRange = timeRangeFactory(
                asDate(rl.night),
                addDays(asDate(rl.night), 1),
            );
            const applies = getAccommodationRateOptionType(rl.option.applies);
            if (applies === "PER_NIGHT") {
                title = joinTranslatables([
                    title,
                    " - ",
                    `${dateTimeFormatter("sv").formatDateMedium(rl.night)}`,
                ]);
            }

            const quantity = rl.quantity;

            const lineItem = orderItemAccommodationRateFactory_V2({
                meta: metaContentFactory({
                    title: title,
                }),
                parentId: room._id,
                providerId: product.providerId,
                quantity: {
                    amount: accommodation.amount,
                    value: quantity,
                },
                messageToProvider: null,
                discounts: [],
                //Current workaround bugfix
                price:
                    rl.option.appliesTo === "ROOM"
                        ? multiplyPrice(rl.totalPrice, quantity)
                        : rl.totalPrice, //multiplyPrice(rl.totalPrice, quantity),
                pricePerItem: rl.pricePerItem,
                productId: product._id,
                skuId: product.skus[0]._id,
                time: nightRange,
                rateId: validRate.rateWithPrices.rateId,
            });
            return lineItem;
        });
        return lineItem;
    });

    return [room, ...allRates];
};

const calculateAvailability = (params: {
    timeRange: TimeRange;
    quantity: number;
    resourceIds: ResourceId[];
    reservations: ResourceReservationRequestInfo[];
    globalResources: ResourceGraph[];
    quantities?: AccommodationQuantities;
    settings: AccommodationAvailabilitySettings;
    options?: { overrideSession: boolean };
}) => {
    const {
        timeRange,
        quantity,
        resourceIds,

        globalResources,
        reservations,
        settings,
    } = params;

    const flatGraph = globalResources.flatMap(findAllGraphs);

    const resourcesAvailability = resourceIds
        .map(resourceId => {
            const resource = flatGraph.find(
                item => item.resourceGroup._id === resourceId,
            );

            if (!resource) throw new Error("Resource not found :" + resourceId);
            //  const resource = res.find(item => item._id === resourceId);

            const requestedQuantity = quantity;

            if (!resource) return undefined;
            if (typeof requestedQuantity === "undefined") return undefined;

            //Remove the resources not matching

            const requestedQuantities = new Map([
                [resourceId, requestedQuantity],
            ]);

            const resourceAvailability = calculateAvailabilitySpecialized(
                [resource.resourceGroup],
                requestedQuantities,
                flatGraph,
                [{ resourceId, timeRanges: [timeRange] }],
                reservations,
                settings.settings,
                {
                    allowSplitBookings: false,
                    overrideSession: params.options?.overrideSession,
                },
            );
            //As we only check one resource we can just take the first one
            const first = resourceAvailability[0];
            if (!params.quantities) return first;
            //Check if the available resources are within the capacity
            return checkForCapacity(first, quantity, params.quantities);
        })
        .filter(exists);

    const result: TimeRangeResourceAvailability = {
        timeRange,
        resourcesAvailability,
    };
    return result;
};

const checkForCapacity = (
    resourceAvailability: ResourceAvailability,
    requestedResourceQuantity: number,
    quantities: {
        [key in AccommodationQuantityType]: number;
    },
): ResourceAvailability & {
    availableResources: ResourceAvailability["availableSubResources"];
} => {
    // QUICK FIX
    // TODO! TAKE ANOTHER LOOK AT THIS TO MAKE SURE IT WORKS AND WORKS PERFORMANT
    const filtered = getPossibilities({
        availableResources: resourceAvailability.availableSubResources.filter(
            isResourceAccommodation,
        ),
        numberOfResources: requestedResourceQuantity,
        requestedQuantities: quantities,
    }).firstPossibleCombination;
    // const filtered = resourceAvailability.availableSubResources.filter(
    //     r =>
    //         isResourceAccommodationWithinCapacity(
    //             r,
    //             requestedResourceQuantity,
    //             quantities,
    //         ) === true,
    // );

    return {
        ...resourceAvailability,
        availableResources: filtered,
        available: filtered.length > 0,
    };
};

const getAccommodationSettings = (product: Product, timeRange?: TimeRange) => {
    const accommodation = getAccommodationSKU(product);

    if (!accommodation) return undefined;

    if (!timeRange) return accommodation.availability.baseSettings;

    const activeAvailability =
        accommodation.availability.availabilityRanges.find(
            r =>
                timeRange.start.unix >= r.start.unix &&
                timeRange.start.unix <= r.end.unix,
        );
    return (
        activeAvailability?.settings ?? accommodation.availability.baseSettings
    );
};

const getAccommodationSKU = (product: Product) => {
    if (product.acquirableAs !== "ACCOMMODATION") return undefined;
    const accommodation = product.skus[0].acquire;
    if (!accommodation) return undefined;
    if (accommodation._type !== "ACCOMMODATION") return undefined;
    return accommodation;
};

export {
    accommodationOrderItemFactory,
    adjustTimeForAccommodation,
    adjustTimeForAccommodationSettings,
    calculateAvailability,
    getAccommodationSettings,
    getAccommodationSKU,
};
